/**
 * XR Hints Canvas - Keyboard shortcuts display for XR scene
 *
 * Creates a canvas texture showing keyboard shortcuts that can be
 * displayed as a 3D plane in the XR environment. Uses tokens.css
 * variables for styling consistency.
 */

/**
 * Hints canvas configuration
 */
export interface HintsCanvasConfig {
  /** Texture width in pixels */
  textureWidth: number;
  /** Texture height in pixels */
  textureHeight: number;
  /** Font family */
  fontFamily: string;
  /** Glass panel fill color */
  glassFill: string;
  /** Glass panel border color */
  glassBorder: string;
  /** Primary text color */
  textPrimary: string;
  /** Secondary text color */
  textSecondary: string;
  /** Corner radius as fraction of height */
  cornerRadius: number;
}

/**
 * Read CSS token as string
 */
function getCSSString(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Default configuration (from tokens.css)
 */
const DEFAULT_CONFIG: HintsCanvasConfig = {
  textureWidth: 512,
  textureHeight: 420,
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  glassFill: 'rgba(255, 255, 255, 0.08)',
  glassBorder: 'rgba(255, 255, 255, 0.18)',
  textPrimary: 'rgba(255, 255, 255, 0.92)',
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  cornerRadius: 0.06,
};

/**
 * Load configuration from CSS tokens
 */
export function loadHintsConfig(): HintsCanvasConfig {
  return {
    textureWidth: 512,
    textureHeight: 420,
    fontFamily: getCSSString('--font-ui', DEFAULT_CONFIG.fontFamily),
    glassFill: getCSSString('--glass-fill', DEFAULT_CONFIG.glassFill),
    glassBorder: getCSSString('--glass-border', DEFAULT_CONFIG.glassBorder),
    textPrimary: getCSSString('--text-primary', DEFAULT_CONFIG.textPrimary),
    textSecondary: getCSSString('--text-secondary', DEFAULT_CONFIG.textSecondary),
    cornerRadius: DEFAULT_CONFIG.cornerRadius,
  };
}

/**
 * Keyboard shortcut definition
 */
interface Shortcut {
  key: string;
  action: string;
}

/**
 * Keyboard shortcuts to display
 */
const SHORTCUTS: Shortcut[] = [
  { key: 'Space', action: 'Play / Pause' },
  { key: '← →', action: 'Rewind / Forward' },
  { key: '↑ ↓', action: 'WPM Up / Down' },
  { key: 'R', action: 'Restart' },
  { key: 'Esc', action: 'Exit XR' },
];

/**
 * Hints Canvas class for rendering keyboard shortcuts
 */
export class HintsCanvas {
  private canvas: OffscreenCanvas | HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  private config: HintsCanvasConfig;

  constructor(config: HintsCanvasConfig = DEFAULT_CONFIG) {
    this.config = config;

    // Create canvas (OffscreenCanvas if available, fallback to regular)
    if (typeof OffscreenCanvas !== 'undefined') {
      this.canvas = new OffscreenCanvas(config.textureWidth, config.textureHeight);
    } else {
      this.canvas = document.createElement('canvas');
      this.canvas.width = config.textureWidth;
      this.canvas.height = config.textureHeight;
    }

    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context for hints canvas');
    }
    this.ctx = ctx;

    // Initial render
    this.render();
  }

  /**
   * Get the underlying canvas element
   */
  getCanvas(): OffscreenCanvas | HTMLCanvasElement {
    return this.canvas;
  }

  /**
   * Render the hints panel
   */
  render(): void {
    const { textureWidth: w, textureHeight: h } = this.config;
    const r = h * this.config.cornerRadius;

    // Clear canvas
    this.ctx.clearRect(0, 0, w, h);

    // Draw frosted glass background
    this.ctx.beginPath();
    this.ctx.roundRect(0, 0, w, h, r);
    this.ctx.closePath();

    this.ctx.fillStyle = this.config.glassFill;
    this.ctx.fill();

    // Draw border
    this.ctx.strokeStyle = this.config.glassBorder;
    this.ctx.lineWidth = 2;
    this.ctx.stroke();

    // Draw title
    const titleSize = 28;
    const lineHeight = 44;
    const keyWidth = 100;
    const startY = 50;
    const paddingX = 32;

    this.ctx.font = `600 ${titleSize}px ${this.config.fontFamily}`;
    this.ctx.fillStyle = this.config.textPrimary;
    this.ctx.textAlign = 'center';
    this.ctx.fillText('Controls', w / 2, startY);

    // Draw shortcuts
    const shortcutStartY = startY + 40;
    const shortcutFontSize = 22;
    const keyFontSize = 20;

    SHORTCUTS.forEach((shortcut, index) => {
      const y = shortcutStartY + index * lineHeight;

      // Draw key (left side, monospace style)
      this.ctx.font = `500 ${keyFontSize}px ${this.config.fontFamily}`;
      this.ctx.fillStyle = this.config.textPrimary;
      this.ctx.textAlign = 'right';

      // Draw key background pill
      const keyText = shortcut.key;
      const keyMetrics = this.ctx.measureText(keyText);
      const keyBgWidth = keyMetrics.width + 16;
      const keyBgHeight = 28;
      const keyX = paddingX + keyWidth - 8;
      const keyY = y - keyBgHeight / 2 - 4;

      this.ctx.fillStyle = this.config.glassBorder;
      this.ctx.beginPath();
      this.ctx.roundRect(keyX - keyBgWidth, keyY, keyBgWidth, keyBgHeight, 6);
      this.ctx.fill();

      // Draw key text
      this.ctx.fillStyle = this.config.textPrimary;
      this.ctx.fillText(keyText, keyX - 8, y);

      // Draw action (right side)
      this.ctx.font = `400 ${shortcutFontSize}px ${this.config.fontFamily}`;
      this.ctx.fillStyle = this.config.textSecondary;
      this.ctx.textAlign = 'left';
      this.ctx.fillText(shortcut.action, paddingX + keyWidth + 16, y);
    });

    // Draw touch hint at bottom
    const hintY = h - 32;
    const hintFontSize = 18;
    this.ctx.font = `400 ${hintFontSize}px ${this.config.fontFamily}`;
    this.ctx.fillStyle = this.config.textSecondary;
    this.ctx.textAlign = 'center';
    this.ctx.fillText('Pan, zoom, rotate to look around', w / 2, hintY);
  }
}

/**
 * Create a hints canvas with token-based configuration
 */
export function createHintsCanvas(): HintsCanvas {
  const config = loadHintsConfig();
  return new HintsCanvas(config);
}
